import React from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Check, Lock, AlertTriangle, CheckCircle, ClipboardCheck } from 'lucide-react';
import { StepCircle } from './StepCircle';

export interface QAResult {
  qaOperator: string;
  qaStatus: 'pass' | 'fail';
  qaVerifiedAt: string;
  message?: string;
}

interface StepHeaderProps {
  stepNumber: number;
  title: string;
  description: string;
  isCompleted?: boolean;
  sectionId?: string;
  onComplete?: () => void;
  marginTop?: 4 | 5;
  disabled?: boolean;
  disabledReason?: string;
  qaResult?: QAResult | undefined;
  onQAReview?: () => void;
  onReworkComplete?: () => void;
}

export const StepHeader: React.FC<StepHeaderProps> = ({
  stepNumber,
  title,
  description,
  isCompleted = false,
  sectionId,
  onComplete,
  marginTop = 4,
  disabled = false,
  disabledReason,
  qaResult,
  onQAReview,
  onReworkComplete,
}) => {
  const renderButton = () => {
    const button = (
      <Button
        variant={disabled ? 'outline-secondary' : 'outline-success'}
        size="sm"
        onClick={onComplete}
        disabled={disabled}
        className="d-flex align-items-center justify-content-center gap-1 px-2 py-1"
        style={{ minWidth: '130px' }}
      >
        {disabled ? (
          <>
            <Lock size={14} />
            Click to Complete
          </>
        ) : (
          <>
            <Check size={14} />
            Click to Complete
          </>
        )}
      </Button>
    );

    if (disabled && disabledReason) {
      return (
        <OverlayTrigger
          placement="left"
          overlay={
            <Tooltip id={`tooltip-step-${stepNumber}`}>
              <div className="text-start">
                <strong>Requirements:</strong>
                <br />
                {disabledReason}
              </div>
            </Tooltip>
          }
        >
          <span>{button}</span>
        </OverlayTrigger>
      );
    }

    return button;
  };

  const getCircleStatus = (): 'completed' | 'pending' | 'qa-pass' | 'qa-fail' | 'ready-for-qa' => {
    if (!isCompleted) {
      return 'pending';
    }
    if (qaResult?.qaStatus === 'pass') {
      return 'qa-pass';
    }
    if (qaResult?.qaStatus === 'fail') {
      return 'qa-fail';
    }
    return 'ready-for-qa';
  };

  return (
    <div id={sectionId} className={`mb-3 mt-${marginTop}`}>
      <div className="d-flex align-items-center gap-3 p-4 rounded-3 border">
        <StepCircle stepNumber={stepNumber} status={getCircleStatus()} size={48} />
        <div className="flex-grow-1">
          <h4 className="mb-1 fw-bold text-dark">{title}</h4>
          <small className="text-muted">{description}</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isCompleted && qaResult?.qaStatus === 'fail' ? (
            onReworkComplete && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={onReworkComplete}
                className="d-flex align-items-center justify-content-center gap-1 px-2 py-1"
                style={{ minWidth: '130px' }}
              >
                <Check size={14} />
                Rework Complete
              </Button>
            )
          ) : isCompleted ? (
            <Button
              variant="success"
              size="sm"
              disabled
              className="d-flex align-items-center justify-content-center gap-1 px-2 py-1"
              style={{ minWidth: '130px', opacity: 1 }}
            >
              <Check size={14} />
              Completed
            </Button>
          ) : (
            onComplete && renderButton()
          )}

          {isCompleted &&
            (qaResult ? (
              <OverlayTrigger
                placement="left"
                overlay={
                  <Tooltip id={`qa-tooltip-${stepNumber}`}>
                    <div className="text-start">
                      <strong>{qaResult.qaStatus === 'pass' ? 'QA Passed:' : 'QA Failed:'}</strong>
                      <br />
                      Operator: {qaResult.qaOperator}
                      <br />
                      {qaResult.message && (
                        <>
                          Message: {qaResult.message}
                          <br />
                        </>
                      )}
                      {qaResult.qaStatus === 'pass' ? 'Verified' : 'Failed'}:{' '}
                      {new Date(qaResult.qaVerifiedAt).toLocaleDateString()}
                      {qaResult.qaStatus === 'pass' && onQAReview && (
                        <>
                          <br />
                          <em>Click to edit</em>
                        </>
                      )}
                    </div>
                  </Tooltip>
                }
              >
                <Button
                  variant={qaResult.qaStatus === 'pass' ? 'success' : 'danger'}
                  size="sm"
                  disabled={qaResult.qaStatus === 'fail'}
                  className="d-flex align-items-center justify-content-center gap-1 px-2 py-1"
                  style={{
                    cursor: qaResult.qaStatus === 'pass' && onQAReview ? 'pointer' : 'default',
                    minWidth: '130px',
                    opacity: 1,
                  }}
                  onClick={qaResult.qaStatus === 'pass' ? onQAReview : undefined}
                >
                  {qaResult.qaStatus === 'pass' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                  QA {qaResult.qaStatus === 'pass' ? 'Pass' : 'Fail'}
                </Button>
              </OverlayTrigger>
            ) : (
              onQAReview && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={onQAReview}
                  className="d-flex align-items-center justify-content-center gap-1 px-2 py-1"
                  style={{ minWidth: '130px' }}
                >
                  <ClipboardCheck size={14} />
                  QA Review
                </Button>
              )
            ))}
        </div>
      </div>
    </div>
  );
};
